<template>
  <div class="page-shell">
    <template v-if="isLoading">
      <div class="section-card">
        <v-skeleton-loader
          type="heading, subtitle, paragraph"
          style="border-radius: 18px; background: transparent"
        />
      </div>
      <div class="section-card">
        <v-skeleton-loader
          type="heading, article"
          style="border-radius: 18px; background: transparent"
        />
      </div>
      <div class="section-card">
        <v-skeleton-loader
          type="heading, list-item-two-line, list-item-two-line"
          style="border-radius: 18px; background: transparent"
        />
      </div>
      <div class="section-card">
        <v-skeleton-loader
          type="heading, list-item-two-line, list-item-two-line, list-item-two-line"
          style="border-radius: 18px; background: transparent"
        />
      </div>
    </template>

    <template v-else>
      <div v-if="loadError" class="section-card error-card">
        <v-alert type="error" variant="tonal">
          {{ loadError }}
        </v-alert>
      </div>

      <template v-else>
        <div class="mt-4">
          <DetailPageHeader
            :item="{
              title: { label: 'Appontment#', value: appointment.appointment_no },
              status: { label: 'Status', value: appointment.status },
            }"
          />
        </div>

        <AppointmentRescheduleRequestCard
          v-if="appointment.reschedule_request?.is_requested"
          :appointment="appointment"
        />

        <div>
          <AppointmentCustomerInfoCard :customer-info="appointment.customer_info" />
        </div>

        <AppointmentCancellationRequestCard
          v-if="appointment.cancellation_request?.is_requested"
          :appointment="appointment"
        />

        <AppointmentScheduleDetailsCard :appointment="appointment" />

        <AppointmentAssignedProfessionalCard
          v-if="appointment?.service_provider"
          :appointment="appointment"
          @assigned="handleAssignedProfessional()"
        />

        <AppointmentPaymentSummary :appointment="appointment" />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { fetchVendorAppointmentDetails } from "@/shared/services/appointments.api";
import AppointmentCustomerInfoCard from "@/shared/components/AppointmentCustomerInfoCard.vue";
import AppointmentAssignedProfessionalCard from "@/modules/vendor/components/AppointmentAssignedProfessionalCard.vue";
import AppointmentScheduleDetailsCard from "@/modules/shared/components/AppointmentScheduleDetailsCard.vue";
import AppointmentRescheduleRequestCard from "@/modules/vendor/components/AppointmentRescheduleRequestCard.vue";
import AppointmentCancellationRequestCard from "@/modules/vendor/components/AppointmentCancellationRequestCard.vue";
import AppointmentPaymentSummary from "@/modules/shared/components/AppointmentPaymentSummary.vue";
import DetailPageHeader from "@/modules/shared/components/DetailPageHeader.vue";

const route = useRoute();

type AppointmentDetail = {
  id: number | string;
  appointment_no: string;
  status: string;
  created_at: string;
  vendor_id?: number | string;
  client_id?: number | string;
  company_id?: number | string;
  cancellation_request: {
    is_requested: boolean;
    reason: string | null;
  };
  reschedule_request: {
    is_requested: boolean;
    preferred_date: string | null;
    preferred_time: string | null;
    reason: string | null;
  };
  service_provider?: {
    id: number | string;
    name: string;
    email: string;
    phone_no: string;
    avatar: string | null;
  } | null;
  customer_info: {
    id: number | string;
    name: string;
    email: string;
    phone_no: string;
    avatar: string | null;
  } | null;
  schedule_info: {
    appointment_date: string | null;
    appointment_time: string | null;
    note: string | null;
  } | null;
  service_address: {
    add1: string | null;
    add2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
  total_info: {
    is_paid: number | boolean;
    sub_total: string | number | null;
    tax_amount: string | number | null;
    total_amount: string | number | null;
  } | null;
  quotation: unknown;
  payment_info: {
    id: number | string;
    payment_code: string;
    amount: string | number;
    gateway: string;
    payment_type: string;
    card_last4: string | null;
    cardholder_name: string | null;
    transaction_id: string | null;
    ref_no: string | null;
    created_at: string;
  } | null;
};

const isLoading = ref(false);
const loadError = ref<string | null>(null);
const appointment = ref<AppointmentDetail>({
  id: 0,
  appointment_no: "",
  status: "",
  created_at: "",
  customer_info: null,
  schedule_info: null,
  service_address: null,
  total_info: null,
  quotation: null,
  payment_info: null,
  vendor_id: null,
  client_id: null,
  company_id: null,
  cancellation_request: {
    is_requested: false,
    reason: null,
  },
  reschedule_request: {
    is_requested: false,
    preferred_date: null,
    preferred_time: null,
    reason: null,
  },
});

async function loadAppointmentDetails() {
  const id = String(route.params.id ?? "");
  if (!id) return;
  try {
    isLoading.value = true;
    loadError.value = null;
    const response = await fetchVendorAppointmentDetails(id);
    // eslint-disable-next-line no-console
    console.log("fetchVendorAppointmentDetails response:", { response });

    const data = (response as any)?.data ?? response;
    if (data) {
      appointment.value = {
        id: data.id,
        appointment_no: String(data.appointment_no ?? ""),
        status: String(data.status ?? ""),
        created_at: String(data.created_at ?? ""),
        customer_info: data.customer_info ?? null,
        schedule_info: data.schedule_info ?? {
          appointment_date: null,
          appointment_time: null,
          note: null,
        },
        service_address: data.service_address ?? null,
        total_info: data.total_info ?? null,
        quotation: data.quotation ?? null,
        payment_info: data.payment_info ?? null,
        vendor_id: data.vendor_id ?? null,
        client_id: data.client_id ?? null,
        company_id: data.company_id ?? null,
        cancellation_request: data.cancellation_request ?? {
          is_requested: false,
          reason: null,
        },
        reschedule_request: data.reschedule_request ?? {
          is_requested: false,
          preferred_date: null,
          preferred_time: null,
          reason: null,
        },
        service_provider: data.service_provider
          ? {
              id: String(data.service_provider.id ?? ""),
              name: String(data.service_provider.name ?? ""),
              email: String(data.service_provider.email ?? ""),
              phone_no: String(data.service_provider.phone_no ?? ""),
              avatar: data.service_provider.avatar ?? null,
            }
          : null,
      };
    }
  } catch (error) {
    loadError.value = (error as any)?.message || "Failed to load appointment";
    // eslint-disable-next-line no-console
    console.error("fetchVendorAppointmentDetails failed", error);
  } finally {
    isLoading.value = false;
  }
}

async function handleAssignedProfessional() {
  // After assigning a professional, refresh appointment details from API.
  await loadAppointmentDetails();
}

onMounted(loadAppointmentDetails);

const appointmentTimelineItems: ReadonlyArray<{
  title: string;
  time: string;
  state: "done" | "active" | "pending";
}> = [
  { title: "Booking Confirmed", time: "1:45 PM", state: "done" },
  { title: "Payment Completed", time: "1:48 PM", state: "done" },
  { title: "Provider Assigned", time: "1:52 PM", state: "active" },
  { title: "On The Way", time: "-", state: "pending" },
  { title: "Arrived", time: "-", state: "pending" },
  { title: "Service Started", time: "1:45 PM", state: "pending" },
  { title: "Service Completed", time: "1:45 PM", state: "pending" },
];
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
}

.section-card {
  background: #fff;
  border-radius: 18px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.error-card {
  border-color: #fecaca;
  background: #fffafa;
}

/* .service-chip {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  font-size: var(--font-size-default);
  border-radius: 999px;
  background: #ffedd5;
  color: #f97316;
  font-weight: 700;
} */

.service-details {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.service-text {
  margin: 0;
  color: #475569;
  font-size: var(--font-size-default);
  line-height: 1.5;
}

.service-includes {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.includes-title {
  margin: 0;
  font-size: var(--font-size-default);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f97316;
  font-weight: 700;
}

.includes-list {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: var(--font-size-default);
  display: grid;
  gap: 6px;
}

.pro-details {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pro-detail-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pro-detail-icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #fff1f2;
  color: #f97316;
}

.pro-detail-label {
  margin: 0;
  font-size: var(--font-size-default);
  color: #94a3b8;
  font-weight: 600;
}

.pro-detail-value {
  margin: 2px 0 0;
  font-size: var(--font-size-default);
  color: #0f172a;
  font-weight: 700;
}

.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}

.status-pill--confirmed {
  color: #16a34a;
  background: #dcfce7;
}

.result-info {
  margin: 0;
  font-size: var(--font-size-default);
  font-weight: 500;
  color: var(--color-subtitle, #475569);
}

.result-error {
  margin: 0;
  font-size: var(--font-size-default);
  font-weight: 600;
  color: #dc2626;
}
</style>
